/**
 * Decoration pass — place decorative objects with natural distribution.
 *
 * Supports:
 * - Poisson-disk scatter for evenly-spaced random placement
 * - Edge placement along terrain boundaries
 * - Clustered placement around a point
 * - Layer-aware placement via tileLayers map (tileId → 'ground'|'objects'|'overhead')
 */
export class DecorationPass {
  /**
   * @param {import('../SeededRandom.js').SeededRandom} rng
   */
  constructor(rng) {
    this.rng = rng;
  }

  /**
   * Resolve which grid a tile should be placed on.
   * @param {Object} grids — { ground, objects, overhead }
   * @param {number} tileId
   * @param {Object} [tileLayers] — tileId → layer name mapping
   * @returns {Array<Array<number>>} target grid
   */
  _resolveGrid(grids, tileId, tileLayers) {
    if (tileLayers && tileLayers[tileId]) {
      const layer = tileLayers[tileId];
      if (layer === 'ground' && grids.ground) return grids.ground;
      if (layer === 'overhead' && grids.overhead) return grids.overhead;
    }
    return grids.objects || grids;
  }

  /**
   * Scatter decorations across the map using a fast Poisson-disk approximation.
   *
   * @param {Array<Array<number>>|Object} objectGridOrGrids — object layer (legacy) or { ground, objects, overhead }
   * @param {Array<Array<number>>} groundGrid — ground layer (read-only)
   * @param {Array<{tileId: number, density: number, spacing: number, validGround: number[], avoidEdges: boolean}>} decorations
   * @param {{width: number, height: number, tileLayers: Object}} options
   */
  scatter(objectGridOrGrids, groundGrid, decorations, options = {}) {
    const grids = this._normalizeGrids(objectGridOrGrids);
    const objectGrid = grids.objects || objectGridOrGrids;
    const width = options.width || objectGrid[0].length;
    const height = options.height || objectGrid.length;
    const { tileLayers } = options;

    for (const deco of decorations) {
      const { tileId, density = 0.02, spacing = 2, validGround, avoidEdges = false } = deco;
      const targetGrid = this._resolveGrid(grids, tileId, tileLayers);

      // Approximate Poisson disk: subdivide into cells of size spacing,
      // then place one candidate per cell with probability = density
      const cellSize = Math.max(1, spacing);

      for (let cy = 0; cy < height; cy += cellSize) {
        for (let cx = 0; cx < width; cx += cellSize) {
          if (!this.rng.chance(density * cellSize * cellSize)) continue;

          // Pick random position within cell
          const x = cx + this.rng.nextInt(0, Math.min(cellSize - 1, width - cx - 1));
          const y = cy + this.rng.nextInt(0, Math.min(cellSize - 1, height - cy - 1));

          if (x >= width || y >= height) continue;
          if (targetGrid[y][x] !== 0) continue;

          // Check valid ground
          if (validGround && validGround.length > 0) {
            if (!validGround.includes(groundGrid[y][x])) continue;
          }

          // Avoid edges
          if (avoidEdges && (x <= 1 || y <= 1 || x >= width - 2 || y >= height - 2)) continue;

          // Check spacing to existing placed objects of same type
          if (!this._checkSpacing(targetGrid, x, y, tileId, spacing, width, height)) continue;

          targetGrid[y][x] = tileId;
        }
      }
    }
  }

  /**
   * Place decorations along terrain edges (where groundGrid changes value).
   *
   * @param {Array<Array<number>>|Object} objectGridOrGrids
   * @param {Array<Array<number>>} groundGrid
   * @param {number} edgeTileId — the ground tile whose edge we follow
   * @param {Array<{tileId: number, chance: number, spacing: number}>} decorations
   * @param {Object} [tileLayers] — tileId → layer name mapping
   */
  placeAlongEdges(objectGridOrGrids, groundGrid, edgeTileId, decorations, tileLayers) {
    const grids = this._normalizeGrids(objectGridOrGrids);
    const objectGrid = grids.objects || objectGridOrGrids;
    const width = objectGrid[0].length;
    const height = objectGrid.length;

    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        if (groundGrid[y][x] !== edgeTileId) continue;

        // Is this an edge cell? (at least one 4-neighbour differs)
        const isEdge =
          groundGrid[y - 1]?.[x] !== edgeTileId ||
          groundGrid[y + 1]?.[x] !== edgeTileId ||
          groundGrid[y][x - 1] !== edgeTileId ||
          groundGrid[y][x + 1] !== edgeTileId;

        if (!isEdge) continue;
        if (objectGrid[y][x] !== 0) continue;

        for (const deco of decorations) {
          if (!this.rng.chance(deco.chance || 0.15)) continue;
          const targetGrid = this._resolveGrid(grids, deco.tileId, tileLayers);
          if (!this._checkSpacing(targetGrid, x, y, deco.tileId, deco.spacing || 2, width, height)) continue;
          targetGrid[y][x] = deco.tileId;
          break; // Only one decoration per cell
        }
      }
    }
  }

  /**
   * Place clustered decorations around a point.
   *
   * @param {Array<Array<number>>|Object} objectGridOrGrids
   * @param {number} centerX
   * @param {number} centerY
   * @param {Array<{tileId: number, count: number}>} decorations
   * @param {number} radius
   * @param {Object} [tileLayers] — tileId → layer name mapping
   */
  clusterAround(objectGridOrGrids, centerX, centerY, decorations, radius, tileLayers) {
    const grids = this._normalizeGrids(objectGridOrGrids);
    const objectGrid = grids.objects || objectGridOrGrids;
    const width = objectGrid[0].length;
    const height = objectGrid.length;

    for (const deco of decorations) {
      const targetGrid = this._resolveGrid(grids, deco.tileId, tileLayers);
      let placed = 0;
      const maxAttempts = deco.count * 5;

      for (let attempt = 0; attempt < maxAttempts && placed < deco.count; attempt++) {
        // Random point within radius (uniform in disk)
        const angle = this.rng.nextFloat(0, Math.PI * 2);
        const dist = Math.sqrt(this.rng.next()) * radius;
        const x = Math.round(centerX + Math.cos(angle) * dist);
        const y = Math.round(centerY + Math.sin(angle) * dist);

        if (x < 0 || y < 0 || x >= width || y >= height) continue;
        if (targetGrid[y][x] !== 0) continue;

        targetGrid[y][x] = deco.tileId;
        placed++;
      }
    }
  }

  /**
   * Check that no tile of the same type exists within `spacing` cells.
   */
  _checkSpacing(grid, x, y, tileId, spacing, width, height) {
    const r = Math.floor(spacing);
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (dx === 0 && dy === 0) continue;
        const nx = x + dx, ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
        if (grid[ny][nx] === tileId) return false;
      }
    }
    return true;
  }

  /**
   * Normalize first arg: if it's a plain 2D array (legacy), wrap as { objects: grid }.
   * If it already has a .objects property, it's a grids bundle.
   */
  _normalizeGrids(gridOrGrids) {
    if (Array.isArray(gridOrGrids)) return { objects: gridOrGrids };
    return gridOrGrids;
  }
}
